<template>
  <div class="page-wrapper">
    <div class="container-fluid px-0 py-0">
      <div class="main-card shadow-sm">
        <div
          class="card-header-custom border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3"
        >
          <div class="d-flex align-items-center gap-3">
            <h1 class="h4 mb-0 fw-bold text-dark">
              {{ computedTitle.label || "Comisión Directiva" }}
            </h1>
            <span class="badge bg-light text-dark border shadow-sm">
              <i class="fas fa-user-tie text-primary me-1"></i>
              Total: {{ directors.length }}
            </span>
          </div>

          <div class="d-flex gap-2">
            <button
              @click="openModalImportar"
              class="btn btn-primary btn-sm shadow-sm d-flex align-items-center hover-elevate"
            >
              <i class="fas fa-plus-circle me-2"></i> Nuevo
            </button>
          </div>
        </div>

        <div class="p-4 bg-light border-bottom">
          <div class="row">
            <div class="col-md-5">
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0 text-muted">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  v-model="searchTerm"
                  class="form-control border-start-0 ps-0"
                  placeholder="Buscar por nombre, apellido o cargo..."
                />
              </div>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div
            v-if="!loading && directors.length === 0"
            class="text-center py-5"
          >
            <div class="text-secondary mb-3">
              <i class="fas fa-user-tie fa-3x opacity-50"></i>
            </div>
            <p class="text-muted">
              No se encontraron registros para esta sección.
            </p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead class="bg-light text-secondary text-uppercase small">
                <tr>
                  <th class="ps-4 py-1 border-0">Orden</th>
                  <th class="py-1 border-0">Nombre</th>
                  <th class="py-1 border-0">Cargo</th>
                  <th
                    v-if="computedTitle.value === 1"
                    class="py-1 border-0 text-center"
                  >
                    Imagen
                  </th>
                  <th class="pe-4 py-1 border-0 text-end">Acciones</th>
                </tr>
              </thead>

              <tbody v-if="loading">
                <TableSpinner
                  :colspan="computedTitle.value === 1 ? 5 : 4"
                  text="Cargando directores..."
                />
              </tbody>

              <draggable
                v-else
                v-model="directors"
                tag="tbody"
                item-key="id"
                @end="updateOrder"
                handle=".drag-handle"
                ghost-class="ghost-row"
              >
                <template #item="{ element }">
                  <tr :key="element.id">
                    <td
                      class="ps-4 py-1 text-muted drag-handle"
                      style="width: 50px; cursor: move"
                    >
                      <i class="fas fa-grip-vertical"></i>
                    </td>

                    <td class="py-1">
                      <div class="d-flex flex-column">
                        <span class="fw-bold text-dark"
                          >{{ element.lastname }}, {{ element.name }}</span
                        >
                      </div>
                    </td>

                    <td class="py-1">
                      <span
                        class="badge bg-light text-dark border border-secondary-subtle fw-normal"
                      >
                        {{ element.position || "N/A" }}
                      </span>
                    </td>

                    <td
                      v-if="computedTitle.value === 1"
                      class="py-1 text-center"
                    >
                      <div v-if="element.image">
                        <img
                          :src="element.image"
                          alt="Avatar"
                          class="rounded border shadow-sm"
                          style="width: 40px; height: 40px; object-fit: cover"
                        />
                      </div>
                      <span v-else class="text-muted small fst-italic"
                        >Sin imagen</span
                      >
                    </td>

                    <td class="pe-4 py-1 text-end">
                      <ActionDropdown>
                        <button
                          class="dropdown-item d-flex align-items-center"
                          @click="editDirector(element)"
                        >
                          <i class="fas fa-edit text-primary fa-fw me-2"></i>
                          Editar
                        </button>
                        <div class="dropdown-divider"></div>
                        <button
                          class="dropdown-item d-flex align-items-center text-danger"
                          @click="removeDirector(element.id)"
                        >
                          <i class="fas fa-trash-alt fa-fw me-2"></i> Eliminar
                        </button>
                      </ActionDropdown>
                    </td>
                  </tr>
                </template>
              </draggable>
            </table>
          </div>
        </div>

        <div
          class="card-footer-custom p-3 bg-light border-top d-flex justify-content-end"
          v-if="lastPage > 1"
        >
          <DataPagination
            :current-page="currentPage"
            :last-page="lastPage"
            :has-more-pages="hasMorePages"
            @fetch-views="(page) => fetchDirectors(page, computedTitle.value)"
          />
        </div>
      </div>
    </div>

    <ModalFormDirector
      ref="modalFormDirectors"
      :subMenu="computedTitle"
      :director="selectedDirector"
      @import-completed="fetchDirectors(1, computedTitle.value)"
      @export-completed="handleExportCompleted"
      @closeModal="closeModal"
      @close-modal="closeModal"
    />

    <ModalDetails
      v-if="modalData"
      :modalData="modalData"
      @close="modalData = null"
    />
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from "vue";
import { useRoute } from "vue-router";
import { debounce } from "lodash";
import draggable from "vuedraggable";

import { sidebarMenuItemsService } from "@/services/api/sidebarMenuItemsService.js";
import {
  fetchDirectorApi,
  updateDirectorsOrder,
  deleteDirector,
} from "@/services/api/directorService.js";
import userConfirmation from "@/utils/userConfirmation.js";

import ModalFormDirector from "@/components/director/ModalFormDirector.vue";
import ModalDetails from "@/components/precios/ModalDetails.vue";
import DataPagination from "@/components/data-display/DataPagination.vue";

// CAMBIO: Se usa TableSpinner en lugar de LoadingSpinner
import TableSpinner from "@/components/ui/TableSpinner.vue";
import ActionDropdown from "@/components/common/ActionDropdown.vue";

export default {
  name: "DirectorView",
  components: {
    ModalFormDirector,
    ModalDetails,
    draggable,
    DataPagination,
    TableSpinner,
    ActionDropdown,
  },
  setup() {
    const route = useRoute();
    const modalData = ref(null);
    const directors = ref([]);
    const loading = ref(false);
    const error = ref(null);

    const currentPage = ref(1);
    const lastPage = ref(1);
    const hasMorePages = ref(false);
    const perPage = ref(10);

    const searchTerm = ref("");
    const selectedDirector = ref(null);
    const modalFormDirectors = ref(null);

    const computedTitle = computed(() => {
      const parent = sidebarMenuItemsService.find(
        (item) => item.link === "/main/director",
      );
      const selectedMenuItem = parent?.subMenu?.find(
        (item) => item.link === `/main/director/${route.params.subMenu}`,
      );
      return selectedMenuItem || { label: "No encontrado", value: null };
    });

    const fetchDirectors = async (
      page = 1,
      titleValue = computedTitle.value.value,
    ) => {
      loading.value = true;
      try {
        const response = await fetchDirectorApi({
          page,
          perPage: perPage.value,
          search: searchTerm.value,
          order: "order",
          section: titleValue,
        });

        if (response.data) {
          directors.value = response.data.data;
          currentPage.value = response.data.meta.current_page || 1;
          lastPage.value = response.data.meta.last_page || 1;
          hasMorePages.value = currentPage.value < lastPage.value;
        } else {
          error.value = response.data.message;
        }
      } catch (err) {
        error.value = err.response ? err.response.data.message : err.message;
      } finally {
        loading.value = false;
      }
    };

    const updateOrder = async () => {
      directors.value.forEach((director, index) => {
        director.order = index + 1;
      });
      try {
        await updateDirectorsOrder(directors.value);
      } catch (err) {
        console.error("Error al actualizar el orden:", err);
      }
    };

    const editDirector = (director) => {
      selectedDirector.value = null;
      nextTick(() => {
        selectedDirector.value = director;
        modalFormDirectors.value.showModal();
      });
    };

    const openModalImportar = () => {
      selectedDirector.value = null;
      nextTick(() => {
        modalFormDirectors.value.showModal();
      });
    };

    const closeModal = () => {
      modalFormDirectors.value.hideModal();
    };

    const handleExportCompleted = async () => {
      await fetchDirectors(currentPage.value, computedTitle.value.value);
    };

    const removeDirector = async (cardId) => {
      const confirmed = await userConfirmation(
        "Está seguro de eliminar la Card seleccionada",
      );
      if (!confirmed) return;
      try {
        await deleteDirector(cardId);
        fetchDirectors(currentPage.value, computedTitle.value.value);
      } catch (error) {
        console.error("Error eliminando card:", error);
      }
    };

    const debouncedSearch = debounce(() => {
      fetchDirectors(currentPage.value, computedTitle.value.value);
    }, 500);

    watch(searchTerm, () => {
      debouncedSearch();
    });

    watch(
      computedTitle,
      (newTitle) => {
        if (newTitle.value) {
          searchTerm.value = "";
          fetchDirectors(1, newTitle.value);
        }
      },
      { immediate: true },
    );

    return {
      directors,
      loading,
      error,
      currentPage,
      lastPage,
      hasMorePages,
      searchTerm,
      computedTitle,
      fetchDirectors,
      updateOrder,
      removeDirector,
      openModalImportar,
      closeModal,
      handleExportCompleted,
      modalFormDirectors,
      editDirector,
      selectedDirector,
      modalData,
    };
  },
};
</script>

<style scoped>
/* Los estilos se mantienen igual */
.page-wrapper {
  background-color: #f8f9fa;
  min-height: 100vh;
}
.main-card {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
}
.card-header-custom {
  padding: 1.5rem 2rem;
  background-color: white;
}
.card-footer-custom {
  background-color: #f8f9fa;
}

.input-group-text {
  background-color: white;
  border-color: #ced4da;
}
.form-control {
  border-color: #ced4da;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
}
.form-control:focus {
  box-shadow: none;
  border-color: #86b7fe;
  background-color: #fbfdff;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}
.ghost-row {
  opacity: 0.5;
  background: #c8ebfb;
}
.bg-primary-subtle {
  background-color: #cfe2ff !important;
  color: #084298 !important;
}
.fa-fw {
  width: 1.25em;
  text-align: center;
}

.btn-white {
  background-color: white;
  color: #495057;
}
.btn-white:hover {
  background-color: #e9ecef;
}
.hover-elevate:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  transition: all 0.2s;
}

@media (max-width: 768px) {
  .card-header-custom {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
